import type { PrinterCaps } from '@renderer/utils/printUtils'

/**
 * Human-facing media labels for printers.
 *
 * A remote printer picker that lists "DS-RX1" and "BP-LITE 80D+80X Printer"
 * tells an operator nothing — those are driver strings. What they recognise is
 * the paper: the small receipt roll versus the 4x6 photo stock. These helpers
 * turn a printer's reported default page size into that label.
 */

interface KnownSize {
  label: string
  widthMm: number
  heightMm: number
}

/**
 * Photo stocks we ship against. Matched on the unordered pair, so a driver
 * reporting landscape or portrait resolves to the same label.
 */
const KNOWN_SIZES: KnownSize[] = [
  { label: '2R', widthMm: 64, heightMm: 89 },
  { label: '4R · 4×6', widthMm: 102, heightMm: 152 },
  { label: '5R · 5×7', widthMm: 127, heightMm: 178 },
  { label: '6×8', widthMm: 152, heightMm: 203 },
  { label: '6×14 panoramic', widthMm: 152, heightMm: 356 },
  { label: '6×20 panoramic', widthMm: 152, heightMm: 508 }
]

/** Drivers round their page sizes inconsistently; a few mm either way is the same stock. */
const SIZE_TOLERANCE_MM = 4

/**
 * Widest web we treat as a receipt roll rather than a sheet. 58mm and 80mm
 * thermal units both land well under this; the smallest photo stock (2R, 64mm)
 * would too, which is why the known-size table is consulted first.
 */
const RECEIPT_MAX_WIDTH_MM = 90

function matchesKnown(size: KnownSize, shortMm: number, longMm: number): boolean {
  const knownShort = Math.min(size.widthMm, size.heightMm)
  const knownLong = Math.max(size.widthMm, size.heightMm)
  return (
    Math.abs(knownShort - shortMm) <= SIZE_TOLERANCE_MM &&
    Math.abs(knownLong - longMm) <= SIZE_TOLERANCE_MM
  )
}

/**
 * Describe what a printer is loaded with, e.g. "4R · 4×6 · 102 × 152 mm" or
 * "80 mm thermal". Returns undefined when the driver reports no default page
 * size — better to show nothing than to invent a stock the operator will trust.
 */
export function describePrinterMedia(caps: PrinterCaps | null | undefined): string | undefined {
  const size = caps?.defaults?.pageSize
  if (!size) return undefined

  const widthMm = Math.round(size.widthMicrons / 1000)
  const heightMm = Math.round(size.heightMicrons / 1000)
  if (!widthMm || !heightMm) return undefined

  const shortMm = Math.min(widthMm, heightMm)
  const longMm = Math.max(widthMm, heightMm)
  const dims = `${shortMm} × ${longMm} mm`

  const known = KNOWN_SIZES.find((candidate) => matchesKnown(candidate, shortMm, longMm))
  if (known) return `${known.label} · ${dims}`

  // Receipt roll: the long edge is only the driver's default cut length, not a
  // real sheet, so quoting it would be misleading. The web width is the useful bit.
  if (shortMm <= RECEIPT_MAX_WIDTH_MM) return `${shortMm} mm thermal`

  return dims
}
